import { Clipboard } from '@angular/cdk/clipboard';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AlertOptions } from '@ionic/core';
import { Subscription } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { ModalDismissRoleEnum } from 'src/app/task-process/answer-editor/send-and-popover/modal-dismiss-role.enum';
import { environment } from 'src/environments/environment';

import { AlcmeonContextService } from '../../core/alcmeon-context/alcmeon-context.service';
import { BasicService } from '../../shared/services/basic.service';
import { ScrollService } from '../../shared/services/scroll.service';
import { ArchiveAndOtherActionsService } from '../archive-and-other-actions/archive-and-other-actions.service';
import { PublicContextAndTaskActionsService } from '../public-context-and-task-actions/public-context-and-task-actions.service';
import {
  messageIdFromTaskOrActionId,
  taskIdFromTaskOrActionId,
  taskOrActionIdFromAnswerActionId,
  taskOrActionIdFromTaskId,
} from '../shared/id-conversions';
import {
  AnswerAction,
  AnyTaskOrActionType,
  PublicContextMessage,
  Task,
  TaskOrActionType,
  TaskOrActionWithMessage,
} from '../shared/model';
import { TaskProcessContext } from '../shared/task-process-context.class';
import { TaskProcessService } from '../shared/task-process.service';
import { deleteActionsForSiteTypeEnum } from './delete-actions-for-site-type.enum';
import { DeleteMessageInTaskProcessComponent } from './delete-message-in-task-process-popover/delete-message-in-task-process-popover.component';

/**
 * This component is used in different places:
 * 1) The main history of context messages, on TaskProcessPage
 * 2) The help requests/anwers and annotations modal
 * 3) The message actions log modal
 * 4) The public context modal
 */
@Component({
  selector: 'app-message-in-task-process',
  templateUrl: './message-in-task-process.component.html',
  styleUrls: ['./message-in-task-process.component.scss'],
})
export class MessageInTaskProcessComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() taskProcessContext: TaskProcessContext;

  // Only the last MessageInTaskProcess of the last chunk triggers the scroll
  // (even if it is not the processed task), so that the whole page is already rendered
  // when the scroll is triggered
  @Input() shouldScrollToTask = false;

  // A couple of features are only active on the main task process page (and not in the modals)
  @Input() isTaskProcessPageMessageList = false; // optional

  // annotations and help requests/replies are clickable on their modal
  @Input() isAnnotationOrHelpModal = false; // optional

  // Optional, triggers the "public context" mode, with the avatar on the left
  @Input() isPublicContextModal?: boolean;

  // This is a setter so that we can refresh the task state when we receive a 'ws.task' websocket notif about this message
  @Input() set message(message: AnyTaskOrActionType) {
    this._message = message;
    this.setUIProperties();
  }

  get message(): AnyTaskOrActionType {
    return this._message;
  }

  @Output() scrolledToProcessedTask = new EventEmitter<void>();
  @Output() retrySending = new EventEmitter<AnswerAction>();

  @HostBinding('class.is-hidden') isHidden: boolean;
  @HostBinding('class.alc-blink') isBlinking = false;

  isPublic: boolean;
  avatarUrl: string;
  bubbleCssClasses: string[];
  messageStatusBluePart: string; // ex: 'Public'
  messageStatusGreyPart: string; // ex 'Message privé - archivé par vous'
  filledStars: void[] = []; // rating score
  emptyStars: void[] = []; // remaining stars to rating scale
  scrollConversationConfig: { onClickFn; labelKey }; // for scroll to reply / replied message

  TaskOrActionType = TaskOrActionType;
  _message: AnyTaskOrActionType;

  // If this TaskOrAction is anterior to the processed message and is on hold by a human user,
  // it needs to display the «archive it» and «leave it» buttons
  isPreviousOnHoldNotByBotIds: boolean;
  isUserReadOnly: boolean;
  isTwitterPublicContext: boolean;

  private subscriptions = new Subscription();

  constructor(
    private alcmeonContext: AlcmeonContextService,
    private alcmeonContextService: AlcmeonContextService,
    private archiveAndOtherActionsService: ArchiveAndOtherActionsService,
    private basicService: BasicService,
    private clipboard: Clipboard,
    private publicContextAndTaskActionsService: PublicContextAndTaskActionsService,
    private scrollService: ScrollService,
    public elRef: ElementRef,
    public taskProcessService: TaskProcessService
  ) {}

  ngOnInit() {
    this.updateScrollConversationConfig();
    if (this.isTaskProcessPageMessageList) {
      this.isPreviousOnHoldNotByBotIds = this.taskProcessContext.isPreviousOnHoldNotByBot(this.message.id);
    }
    if (this.isTaskOrActionWithMessage() && this.isTaskProcessPageMessageList) {
      this.avatarUrl = (this.message as TaskOrActionWithMessage).fromUserAvatarUrl;
    }
    this.taskProcessContext.doBlinkBubble$.pipe(filter(id => id === this.message?.id)).subscribe(() => this.doBlink());
    this.isUserReadOnly = this.alcmeonContextService.isLoggedUserReadOnlyForSite(this.taskProcessContext.site.id);

    this.isTwitterPublicContext =
      this.taskProcessContext.site.type === SiteTypeEnum.Twitter &&
      !this.taskProcessContext.processedTaskInfo.isPrivate;
  }

  ngAfterViewInit() {
    if (this.shouldScrollToTask) {
      this.subscriptions.add(
        this.basicService.appIsVisible$.pipe(first(Boolean)).subscribe(() => {
          // An arbitrary 200ms timout to give images a chance to load,
          // so that hopefully DOM elements won't change their size while the scroll is ongoing
          setTimeout(() => this.taskProcessContext.scrollToProcessedTask(), 200);
          setTimeout(() => this.scrolledToProcessedTask.emit(), 200 + this.scrollService.scrollDurationMs());
        })
      );
    }
  }

  archiveAndScrollBackToProcessedMessage() {
    const taskId = taskIdFromTaskOrActionId(this.message.id);
    this.taskProcessService.acquireOnHoldAndArchive(taskId).subscribe(() => {
      this.taskProcessContext.removeFromPreviousOnHoldNotByBot(this.message.id);
      this.isPreviousOnHoldNotByBotIds = false;
      this.taskProcessContext.scrollToProcessedTask();
    });
  }

  hasTaskActionModalButton(message) {
    return message.type === TaskOrActionType.Task && this.isTaskProcessPageMessageList;
  }

  hasPublicContextModalButton(message) {
    return this.hasTaskActionModalButton(message) && this.isPublic;
  }

  hasDeleteAnswerMessageModalButton(message) {
    return (
      [TaskOrActionType.Answer, TaskOrActionType.Message].includes(message.type) &&
      message.canDeleteAnswerMessage &&
      this.isPublic &&
      !this.isPublicContextModal &&
      !message.isDeleted &&
      this.isTaskProcessPageMessageList
    );
  }

  copyTaskUrl(task: Task | any) {
    const taskUrl = [
      environment.frontendURLPrefix || `${environment.apiURLPrefix}/v3`, // desktop || widget
      '#',
      this.alcmeonContextService.companyId,
      'inbox',
      'task',
      task.id.split(':')[0],
    ].join('/');

    this.clipboard.copy(taskUrl);
    this.basicService.toastSuccess('m/task-process/tooltips/link-copied');
  }

  showTaskActionsModal(task: Task | any) {
    this.publicContextAndTaskActionsService.showPublicContextAndTaskActionsLog(this.taskProcessContext, task, false);
  }

  showPublicContextModal(task: Task | any) {
    this.publicContextAndTaskActionsService.showPublicContextAndTaskActionsLog(this.taskProcessContext, task, true);
  }

  hasActionError() {
    return (
      [
        TaskOrActionType.Answer,
        TaskOrActionType.Message,
        TaskOrActionType.WhatsappTemplate,
        TaskOrActionType.KakaotalkTemplate,
      ].includes(this.message.type) && !!(this.message as AnswerAction).error
    );
  }

  isClosedNotification() {
    return this.message.type === TaskOrActionType.Task && (this.message as Task).closed;
  }

  isByBot() {
    return this.message.type === TaskOrActionType.Answer && this.alcmeonContext.isBot(this.getAdminUser()?.id);
  }

  showActionError() {
    const error = (this.message as AnswerAction).error;
    const options: AlertOptions = {
      subHeader: `${this.basicService.translate(error.translationKey)} (${error.id})`,
      buttons: [this.basicService.translate('m/ok')],
      cssClass: 'user-selectable-alert',
    };
    if (this.alcmeonContext.canLoggedUserSeeActionErrorDetails()) {
      options.message = error.details;
    }
    this.basicService.presentAlert(options);
  }

  onRetryClicked() {
    this.retrySending.emit(this.message as AnswerAction);
  }

  shouldShowScrollConversation() {
    return (this.isTaskProcessPageMessageList || this.isAnnotationOrHelpModal) && this.scrollConversationConfig;
  }

  async onDeleteAnswerMessageClicked(message) {
    const siteId = this.taskProcessContext.site.id;
    const siteType = this.taskProcessContext.site.type;
    const taskId = (this.message as AnswerAction).answeredTaskId;
    const messageId =
      message.type === TaskOrActionType.Message ? messageIdFromTaskOrActionId(message.id) : message.answerMessageId;
    const modalResult = await this.basicService.popoverWithResult(null, DeleteMessageInTaskProcessComponent);
    if (modalResult.role === ModalDismissRoleEnum.OK) {
      this.archiveAndOtherActionsService
        .performModerationAction(siteId, taskId, messageId, deleteActionsForSiteTypeEnum[siteType])
        .subscribe(() => {
          this.basicService.toastSuccess('m/task-process/moderation-action/sucess');
        });
      message.isDeleted = true;
    }
  }

  private setUIProperties() {
    this.updateIsPublicAndIsHidden();
    this.updateMessageStatus();
    this.updateCssClasses();
    this.updateRatingStars();
    this.updateScrollConversationConfig();
  }

  private updateCssClasses() {
    this.bubbleCssClasses = ['message-bubble'];
    if (this.isPublic) {
      this.bubbleCssClasses.push('message-bubble-public');
    }

    if ((this.message as PublicContextMessage).level === 2) {
      this.bubbleCssClasses.push('level-2');
    } else if ((this.message as PublicContextMessage).level >= 3) {
      this.bubbleCssClasses.push('level-3');
    }

    if (this.hasActionError()) {
      this.bubbleCssClasses.push('action-error');
    }
    if (this.isClosedNotification()) {
      this.bubbleCssClasses.push('conversation-closed');
    }
    if (this.isByBot()) {
      this.bubbleCssClasses.push('by-bot');
    }
  }

  /**
   * If message is in the main area or task actions log:
   *  - Annotation, Help: «Demande d'aide de V. Henderson»
   *  - Task:             «PUBLIC : post libre > répondu par v. Henderson»
   *                      «Messenger > Archivé automatiquement»
   *  - Answer:           «PUBLIC : Commentaire > envoyé par v. henderson»
   *                      «Messenger > envoyé par v. henderson»
   *  - Message:          «PUBLIC»
   *                      «Message privé»
   *
   * If message is in the public context:
   *  - Message de la marque:    «PUBLIC» (we don't have a detailedTaskType for such messages)
   *  - Message de l'internaute: «PUBLIC: COMMENTAIRE MUR»
   */
  private updateMessageStatus() {
    this.updateMessageStatusBluePart();
    this.updateMessageStatusGreyPart();
  }

  private updateMessageStatusBluePart() {
    this.messageStatusBluePart = '';
    if (this.isPublic) {
      this.messageStatusBluePart = this.basicService.translate('m/task-process/bubble/public');
      if (this.isHidden) {
        this.messageStatusBluePart += ` - ${this.basicService.translate('m/task-process/bubble/hidden')}`;
      }
    }
  }

  private updateMessageStatusGreyPart() {
    this.messageStatusGreyPart = '';
    const typesWithDetailedTaskType = [TaskOrActionType.Task, TaskOrActionType.Message, TaskOrActionType.Answer];
    const canHaveDetailedTaskType = typesWithDetailedTaskType.includes(this.message.type);
    if (canHaveDetailedTaskType) {
      this.messageStatusGreyPart = this.detailedTaskTypeOrPrivateOrEmpty();
    }
    const stateOrActionKey = this.bubbleTaskStateOrActionTypeKey();
    if (stateOrActionKey) {
      const nameParam = this.getAdminUser() ? { name: this.getAdminUser().name } : null;
      if (this.messageStatusGreyPart) {
        this.messageStatusGreyPart += ' \u203A ';
      }
      this.messageStatusGreyPart += this.basicService.translate(stateOrActionKey, nameParam);
    }
    if (this.messageStatusBluePart && this.messageStatusGreyPart) {
      this.messageStatusGreyPart = ' : ' + this.messageStatusGreyPart;
    }
  }

  private updateIsPublicAndIsHidden() {
    switch (this.message.type) {
      case TaskOrActionType.Task:
      case TaskOrActionType.Answer:
      case TaskOrActionType.Message:
        this.isPublic = !(this.message as TaskOrActionWithMessage).isPrivate;
        this.isHidden = !!(this.message as TaskOrActionWithMessage).isHidden;
        break;
      default:
        this.isPublic = false;
        this.isHidden = false;
        break;
    }
  }

  private updateRatingStars() {
    if ((this.message as Task).rating) {
      const rating = (this.message as Task).rating;
      this.filledStars = [...new Array(rating.value)];
      if (rating.scale) {
        this.emptyStars = [...new Array(rating.scale - rating.value)];
      }
    }
  }

  private updateScrollConversationConfig() {
    if ((this.message as Task).latestAnswerActionId) {
      this.scrollConversationConfig = {
        onClickFn: () => this.scrollToReply(),
        labelKey: 'm/task-process/scroll-to-answer',
      };
    }
    if ((this.message as AnswerAction).answeredTaskId && !this.hasActionError()) {
      this.scrollConversationConfig = {
        onClickFn: () => this.scrollToRepliedTask(),
        labelKey: 'm/task-process/scroll-to-replied-to',
      };
    }
    if (environment.bundledWithAngularJSApp && this.isAnnotationOrHelpModal) {
      // Bubbles in the annotation and help request/answer modals should be clickable
      // and clicking them should scroll the main conversation to the clicked bubble
      this.scrollConversationConfig = {
        onClickFn: () => this.taskProcessContext.scrollToTaskOrAction(this.message.id, true),
        labelKey: 'm/task-process/scroll-to-annotation',
      };
    }
  }

  private scrollToReply() {
    const replyId = taskOrActionIdFromAnswerActionId((this.message as Task).latestAnswerActionId);
    this.taskProcessContext.scrollToTaskOrAction(replyId, true);
  }

  private scrollToRepliedTask() {
    const taskOrActionId = taskOrActionIdFromTaskId((this.message as AnswerAction).answeredTaskId);
    this.taskProcessContext.scrollToTaskOrAction(taskOrActionId, true);
  }

  private doBlink() {
    if (this.isTaskProcessPageMessageList) {
      this.isBlinking = true;
      setTimeout(() => (this.isBlinking = false), 1000);
    }
  }

  // returns the task type for Tasks and Answers; returns the generic "Private message" or an empty string for Messages
  private detailedTaskTypeOrPrivateOrEmpty() {
    const taskType = (this.message as Task).detailedTaskType;
    const defaultIfNoTaskType = this.isPublic ? null : 'm/task-process/bubble/private';
    let key = taskType ? `message/detailed_task_type/${taskType}` : defaultIfNoTaskType;
    if ((this.message as Task).shopProductInfo) {
      key = 'm/task-process/pseudo-task-type/shop-product';
    }
    return key ? this.basicService.translate(key) : '';
  }

  private bubbleTaskStateOrActionTypeKey() {
    if (this.isClosedNotification()) {
      return '';
    }
    switch (this.message.type) {
      case TaskOrActionType.Task:
        return this.bubbleTaskStateKey();
      case TaskOrActionType.Message:
        return null;
      default:
        return this.bubbleOtherActionTypeKey();
    }
  }

  //
  // par exemple la cle de "Archivé par moi"
  //
  private bubbleTaskStateKey() {
    const statePrefix = this.isByMe()
      ? 'm/task-state-with-preposition-and-me/'
      : 'm/task-state-with-preposition-and-name/';
    return statePrefix + (this.message as Task).taskStateCode;
  }

  //
  // par exemple la cle de "Domande d'aide: réponse de {{name}}"
  //
  private bubbleOtherActionTypeKey() {
    if (this.hasActionError()) {
      return 'm/task-action-with-preposition-and-me/action-error';
    }
    const actionPrefix = this.isByMe()
      ? 'm/task-action-with-preposition-and-me/'
      : 'm/task-action-with-preposition-and-name/';
    return actionPrefix + this.message.type;
  }

  private isByMe() {
    return this.alcmeonContext.isMe(this.getAdminUser());
  }

  private getAdminUser() {
    return 'adminUser' in this.message && this.message.adminUser ? this.message.adminUser : null;
  }

  private isTaskOrActionWithMessage(): boolean {
    return [
      TaskOrActionType.Task,
      TaskOrActionType.Message,
      TaskOrActionType.Answer,
      TaskOrActionType.InmessageCarousel,
      TaskOrActionType.WhatsappTemplate,
      TaskOrActionType.KakaotalkTemplate,
    ].includes(this.message.type);
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
  }
}
